import {
  ToQueryStringFilters,
  useOrderByFilter,
  usePagination,
  useToQueryString,
  leadStatusIds,
} from '@/core'
import { Pagination } from '@/core/types'
import { createInjectionState, toReactive } from '@vueuse/core'
import { computed, ref, watch } from 'vue'
import type { TDashboard } from '../../../types'
import { useModuleQuery } from '../query/useModuleQuery'
import { useCatalogQuery } from '../query/useCatalogQuery'
import { storeToRefs } from 'pinia'
import { useCatalogStore } from '@/core/store/catalogs'
import { useI18n } from 'vue-i18n'
import { TUser } from '@/modules/auth'
import { useAuth } from 'vue-auth3'

const [useProvideModule, useModule] = createInjectionState(() => {
  const rows = ref<number[]>([])
  const form = ref<TDashboard>()
  const store = useCatalogStore()
  const auth = useAuth()

  const { t } = useI18n()
  const { catalogs } = storeToRefs(store)
  const filters = ref<ToQueryStringFilters>({
    param: '',
    dates: {
      created_at: [],
    },
    order_by: 'created_at,desc',
  })
  const filtersDinamic = ref<ToQueryStringFilters>({
    param: '',
    dates: {
      created_at: [],
    },
    order_by: 'created_at,desc',
    project_id: [],
    status_id: [],
    advisor_id: [],
    origin_id: [],
    type_campaign_id: [],
    organic_id: [],
    referral_id: [],
  })
  const filtersStatus = ref<ToQueryStringFilters>({
    param: '',
    id: Object.values(leadStatusIds),
  })

  const advisorFilters = ref<ToQueryStringFilters>({
    param: '',
  })
  const projectFilters = ref<ToQueryStringFilters>({
    param: '',
  })
  const lotFilters = ref<ToQueryStringFilters>({
    param: '',
    status_id: [25],
  })
  const statusQueryParams = useToQueryString(toReactive(filtersStatus))

  const advisorsQueryParams = useToQueryString(toReactive(advisorFilters), {
    searchColumns: ['name'],
  })

  const projectsQueryParams = useToQueryString(toReactive(projectFilters), {
    searchColumns: ['name'],
  })

  const lotsQueryParams = useToQueryString(toReactive(lotFilters), {
    searchColumns: ['name'],
  })

  const moduleQueryParams = useToQueryString(toReactive(filters), {
    searchColumns: ['name'],
    dateSettings: {
      enableFormat: true,
    },
  })

  const dinamicQueryParams = useToQueryString(toReactive(filtersDinamic), {
    searchColumns: ['name'],
    dateSettings: {
      enableFormat: true,
    },
  })

  const modulePagination = usePagination()

  const catalogQuery = useCatalogQuery({
    filters: {
      statusesCatalog: statusQueryParams.queryParams,
      projectsCatalog: projectsQueryParams.queryParams,
      advisorsCatalog: advisorsQueryParams.queryParams,
      leadsLotsCatalog: lotsQueryParams.queryParams,
    },
    enableQuery: {
      advisorsCatalog: false,
      campaignsCatalog: false,
      originsCatalog: true,
      statusesCatalog: false,
      projectsCatalog: false,
      lotsCatalog: false,
      socialNetworksCatalog: false,
      referralsCatalog: false,
      leadsLotsCatalog: false,
    },
  })

  const moduleQuery = useModuleQuery({
    currentPage: modulePagination.currentPage,
    filters: {
      loadRecords: moduleQueryParams.queryParams,
    },
    enableQuery: {
      loadRecords: true,
      loadFilters: true,
    },
    handlers: {
      onLoadRecords: (pagination: Pagination) => {
        modulePagination.setPagination(pagination)
      },
    },
  })

  const dinamicQuery = useModuleQuery({
    currentPage: 1,
    filters: {
      loadDinamicRecords: dinamicQueryParams.queryParams,
    },
    enableQuery: {
      loadDinamicRecords: false,
    },
  })

  const areAdminCatalogsLoaded = ref(false)

  const onRequestCatalogs = async (payload: number[]) => {
    await catalogQuery.requestCatalogs.mutateAsync({
      ids: payload,
    })
  }

  const onRequestAdminCatalogs = async () => {
    if (areAdminCatalogsLoaded.value) return
    areAdminCatalogsLoaded.value = true
    await Promise.all([
      catalogQuery.advisorsCatalog.refetch(),
      catalogQuery.campaignsCatalog.refetch(),
      catalogQuery.projectsCatalog.refetch(),
      catalogQuery.leadsLotsCatalog.refetch(),
      catalogQuery.socialNetworksCatalog.refetch(),
      catalogQuery.referralsCatalog.refetch(),
      catalogQuery.statusesCatalog.refetch(),
    ])
  }

  const onRequestDinamicRecords = async () => {
    await dinamicQuery.loadDinamicRecords.refetch()
  }

  watch(
    () => dinamicQueryParams.queryParams.value,
    () => {
      onRequestDinamicRecords()
    },
  )

  const { orderByOptions } = useOrderByFilter({
    columns: ['created_at', 'email'],
  })

  const data = computed(() => moduleQuery.loadRecords.data?.value)
  const dinamicData = computed(
    () => dinamicQuery.loadDinamicRecords.data?.value,
  )
  const isLoading = computed(
    () =>
      moduleQuery.loadRecords.isLoading.value ||
      dinamicQuery.loadDinamicRecords.isLoading.value,
  )

  const advisorsCatalog = computed(
    () => catalogQuery.advisorsCatalog.data?.value,
  )
  const campaignsCatalog = computed(
    () => catalogQuery.campaignsCatalog.data?.value,
  )

  const projectsCatalog = computed(
    () => catalogQuery.projectsCatalog.data?.value,
  )

  const leadsLotsCatalog = computed(
    () => catalogQuery.leadsLotsCatalog.data?.value,
  )

  const socialNetworksCatalog = computed(
    () => catalogQuery.socialNetworksCatalog.data?.value,
  )

  const referralsCatalog = computed(
    () => catalogQuery.referralsCatalog.data?.value,
  )

  const statusesCatalog = computed(() => {
    let data = catalogQuery.statusesCatalog.data.value
    if (!data) return []
    data = data.map((status) => {
      return {
        ...status,
        name: t(`status.${status.name?.toUpperCase()}`),
      }
    })
    return data.sort((a, b) => a.name.localeCompare(b.name))
  })

  const user = computed(() => auth.user<TUser>())

  return {
    data,
    filters,
    filtersDinamic,
    orderByOptions,
    rows,
    form,
    isLoading,
    modulePagination: toReactive(modulePagination),
    advisorsCatalog,
    campaignsCatalog,
    projectsCatalog,
    leadsLotsCatalog,
    statusesCatalog,
    referralsCatalog,
    socialNetworksCatalog,
    catalogs,
    lotFilters,
    user,
    dinamicData,
    onRequestCatalogs,
    onRequestDinamicRecords,
    onRequestAdminCatalogs,
  }
})

export { useModule, useProvideModule }
